import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { AuthenticatedUser, Permission } from '@bakery/shared';

import { ApiError, api, authToken, setAuthToken, setUnauthorizedHandler } from './api.js';
import { OfflineUnavailableError, setTransportUser } from './transport.js';
import { cacheUser, cachedUser, clearCachedUser } from './session-cache.js';

/** A session this device issued itself, when no server was reachable. */
const LOCAL_SESSION_KEY = 'bakery.localSession';

function localSessionToken(): string | null {
  try {
    return localStorage.getItem(LOCAL_SESSION_KEY);
  } catch {
    return null;
  }
}

function setLocalSessionToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(LOCAL_SESSION_KEY, token);
    else localStorage.removeItem(LOCAL_SESSION_KEY);
  } catch {
    /* the session simply will not survive a reload */
  }
}

import { localAuth, startLocalDb, whenLocalDbReady } from '../data/local-db.js';
import { bootstrapLocalData } from '../data/bootstrap.js';
import { startSyncLoop } from '../data/sync.js';

interface AuthState {
  user: AuthenticatedUser | null;
  /** True while the stored token is being checked on start-up. */
  loading: boolean;
  /** The signed-in user still has the password an admin gave them. */
  mustChangePassword: boolean;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Permission check. Admins pass everything. */
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth() used outside AuthProvider');
  return context;
}

/** Shorthand for the common case of gating one thing. */
export function useCan(permission: Permission): boolean {
  return useAuth().can(permission);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    // Cached responses were fetched as someone else. Dropping them stops one
    // user's data from flashing up in the next user's session.
    queryClient.clear();
    clearCachedUser();

    /*
     * The local *session* ends; the stored password verifier does not. That is
     * the whole point: a till locked at closing has to be openable next morning
     * with the internet still down. Use "forget this device" to remove the
     * verifier itself.
     */
    const token = localSessionToken();
    setLocalSessionToken(null);
    if (token) void localAuth('auth.endLocal', {}).catch(() => {});
  }, [queryClient]);

  // A 401 from any request means the session is gone; fall back to the prompt.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      queryClient.clear();
    });
  }, [queryClient]);

  /*
   * Permissions are enforced against the local database too, so the worker has
   * to know who is asking. Kept in step with the session rather than stored in
   * the worker, so signing out cannot leave a stale identity able to read the
   * device's data.
   */
  useEffect(() => {
    setTransportUser(user);
  }, [user]);

  /*
   * Bring up this device's own copy of the shop, once someone is signed in.
   * Deliberately not awaited and never fatal: a device that cannot store data
   * locally still works perfectly well while it can reach the server, and the
   * app should not refuse to start over a missing offline mode.
   */
  useEffect(() => {
    if (!user) return;
    void startLocalDb().then(async (state) => {
      if (!state.ready) {
        console.warn('Offline mode unavailable on this device:', state.reason);
        return;
      }
      // Fill it while there is a connection, so there is something to fall back
      // to later. Failure here is not an error the cashier needs to see.
      const outcome = await bootstrapLocalData(user);
      if (outcome.ran) console.info(`Local copy refreshed: ${outcome.rows} rows`);
    });
  }, [user]);

  /*
   * Exchange changes with the hub while someone is signed in. Started after the
   * database is up rather than alongside it, so the first round is not spent
   * waiting on a boot it could simply have waited for.
   */
  useEffect(() => {
    if (!user) return;
    let stop: (() => void) | null = null;
    let cancelled = false;

    void whenLocalDbReady().then((state) => {
      if (cancelled || !state.ready) return;
      stop = startSyncLoop(user);
    });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [user]);

  // Restore the session on start-up, if the stored token is still good.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!authToken()) {
        /*
         * No server token is not the same as no session. Signing in while the
         * server was away issues one this device can check itself, and a page
         * reload mid-service must not send a cashier back to a login screen
         * they cannot complete.
         */
        const token = localSessionToken();
        if (token) {
          try {
            const resumed = await localAuth<{ user: AuthenticatedUser } | null>('auth.resumeLocal', { token });
            if (!cancelled && resumed?.user) {
              setUser(resumed.user);
              cacheUser(resumed.user);
              setLoading(false);
              return;
            }
          } catch {
            /* the device could not verify it; fall through to the prompt */
          }
          setLocalSessionToken(null);
        }

        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const me = await api.auth.me();
        if (!cancelled) {
          setUser(me);
          cacheUser(me);
        }
      } catch (error) {
        /*
         * Only sign out when the *server* rejected the session. If it simply
         * could not be reached, the session is very likely still good, and
         * throwing the cashier back to a login screen they cannot complete —
         * because logging in needs the server too — would turn an internet
         * outage into a shop that cannot sell at all. That is the exact failure
         * this whole offline effort exists to prevent.
         */
        const rejected = error instanceof ApiError && error.status === 401;
        if (cancelled) return;

        if (rejected) {
          clear();
          clearCachedUser();
        } else {
          const remembered = cachedUser();
          if (remembered) setUser(remembered);
          else clear();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, [clear]);

  const signIn = useCallback(
    async (identifier: string, password: string) => {
      try {
        const result = await api.auth.login(identifier, password);
        setAuthToken(result.token);
        queryClient.clear();
        setUser(result.user);
        cacheUser(result.user);

        /*
         * Teach this device to verify that password on its own, so the same
         * person can sign in here tomorrow with the internet down. Failing to
         * remember must not fail the sign-in that just succeeded — the worst
         * case is that offline sign-in is unavailable until the next one.
         */
        try {
          const state = await whenLocalDbReady();
          if (state.ready) await localAuth('auth.remember', { user: result.user, password });
        } catch (error) {
          console.warn('Could not store an offline credential on this device:', error);
        }
        return;
      } catch (error) {
        /*
         * Only fall back when the server could not be *reached*. A refusal is an
         * answer and must stand — otherwise a disabled account or a changed
         * password could be worked around by pulling the network cable.
         */
        if (!(error instanceof OfflineUnavailableError)) throw error;
      }

      const state = await whenLocalDbReady();
      if (!state.ready) {
        throw new Error(
          'The shop server is not reachable, and this device cannot store data locally, so there is nothing to sign in against.',
        );
      }

      const local = await localAuth<{
        user: AuthenticatedUser;
        lastOnlineAt: string;
        localSession: string;
      }>('auth.localSignIn', { identifier, password });

      /*
       * A local session: no server token, because no server answered. Everything
       * the device can do on its own works; anything needing the hub reports
       * that plainly. The password is deliberately not kept anywhere — when the
       * connection returns, whoever is at the till signs in again and the
       * session becomes a real one.
       */
      setAuthToken(null);
      setLocalSessionToken(local.localSession);
      queryClient.clear();
      setUser(local.user);
      cacheUser(local.user);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch (error) {
      // A already-dead session still ends locally; nothing to recover here.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      clear();
    }
  }, [clear]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const result = await api.auth.changePassword(currentPassword, newPassword);
      // The server revoked every session including this one and issued a fresh
      // token, so swap it in rather than sending the user back to the prompt.
      setAuthToken(result.token);
      setUser(result.user);
      cacheUser(result.user);
      queryClient.clear();
    },
    [queryClient],
  );

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(user?.effectivePermissions ?? []);
    const can = (permission: Permission) => (user?.role === 'admin' ? true : permissions.has(permission));

    return {
      user,
      loading,
      mustChangePassword: user?.mustChangePassword ?? false,
      signIn,
      signOut,
      changePassword,
      can,
      canAny: (...list: Permission[]) => list.some(can),
    };
  }, [user, loading, signIn, signOut, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
